import { authHeaders } from '../../core/api/authHeaders';

/**
 * Approval requests (owner, 30 Oct 2026). Any staff member can ASK a manager to let an
 * order through ("COD without the ₹99 advance", "repeat shipment"); managers decide on the
 * Approval Desk. The proxy decides who may do what — this only transports.
 */
const PROXY = (import.meta.env?.VITE_PROXY_URL || (import.meta.env?.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');

async function call(path, opts = {}) {
  const r = await fetch(`${PROXY}${path}`, { ...opts, headers: { ...(await authHeaders()), 'Content-Type': 'application/json' } });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || j.ok === false) throw new Error(j.error || j.message || `Server returned ${r.status}`);
  return j;
}

export const APPROVAL_LABELS = { cod_fee: 'COD without ₹99 advance', repeat: 'Repeat shipment', discount: 'Discount' };
export const MANAGER_ROLES = new Set(['manager', 'admin', 'super_admin']);
export const isManagerRole = (role) => MANAGER_ROLES.has(String(role || '').toLowerCase());

export const shipRules = (orderKey) => call(`/api/dispatch-gate/ship-rules?orderKey=${encodeURIComponent(orderKey)}`);
export const requestApproval = (orderKey, kind, reason) => call('/api/approvals/request', { method: 'POST', body: JSON.stringify({ orderKey, kind, reason }) });
export const approveRepeat = (orderKey, reason) => call('/api/dispatch-gate/approve-repeat', { method: 'POST', body: JSON.stringify({ orderKey, reason }) });
export const releaseHold = (orderKey, reason) => call('/api/dispatch-gate/hold', { method: 'POST', body: JSON.stringify({ orderKey, hold: false, reason }) });

/** view: 'pending' | 'blocked' | 'approved' | 'rejected' | 'auto' */
export const listApprovals = (view = 'pending') => call(`/api/approvals?view=${encodeURIComponent(view)}`);
export const decideApproval = (id, decision, note) => call('/api/approvals/decide', { method: 'POST', body: JSON.stringify({ id, decision, note }) });
export const allowCodFee = (orderKey, reason) => call('/api/dispatch-gate/approve-cod-fee', { method: 'POST', body: JSON.stringify({ orderKey, reason }) });

/**
 * The one action behind every "COD without ₹99" button: a manager allows it on the spot,
 * anyone else sends a request to the Approval Desk. Returns a line to show the user, or
 * null when they backed out of the reason prompt.
 */
export async function codFeeAction(order, role) {
  const key = order?.firebaseKey || order?.id;
  const manager = isManagerRole(role);
  const reason = window.prompt(manager
    ? `Allow ${order?.id} to ship as COD WITHOUT the ₹99 advance? Reason (goes in the order history):`
    : `Ask a manager to let ${order?.id} ship as COD WITHOUT the ₹99 advance. Why? (the manager sees this)`);
  if (reason === null) return null;
  if (manager) { await allowCodFee(key, reason.trim()); return 'Allowed — this COD order can now go to a courier (after the COD call).'; }
  const r = await requestApproval(key, 'cod_fee', reason.trim());
  return r.already ? 'Already asked — waiting for a manager on the Approval Desk.' : 'Sent to the Approval Desk — a manager will decide.';
}

/**
 * "Repeat shipment within 10 days" from the dispatch row (owner, 31 Oct 2026: there was
 * nowhere to approve DP07539): a manager approves on the spot, anyone else asks.
 */
export async function repeatAction(order, role) {
  const key = order?.firebaseKey || order?.id;
  const manager = isManagerRole(role);
  const reason = window.prompt(manager
    ? `Approve ${order?.id} as a genuine repeat shipment? Reason (goes in the order history):`
    : `Ask a manager to approve ${order?.id} as a genuine repeat shipment. Why?`);
  if (reason === null) return null;
  if (manager) {
    await call('/api/dispatch-gate/approve-repeat', { method: 'POST', body: JSON.stringify({ orderKey: key, reason: reason.trim() }) });
    return 'Approved — book it again.';
  }
  const r = await requestApproval(key, 'repeat', reason.trim());
  return r.already ? 'Already asked — waiting for a manager on the Approval Desk.' : 'Sent to the Approval Desk — a manager will decide.';
}

/** Minutes left before staff may ask to ship a COD order without its advance (0 = now). */
export const codRequestWaitMin = (order, now = Date.now()) => {
  const pl = order?.payment?.paymentLink || {};
  if (!pl.url || ['PAID', 'CANCELLED', 'EXPIRED'].includes(String(pl.status || '').toUpperCase())) return 0;
  const sent = Math.max(Number(pl.createdAt) || 0, Number(pl.lastSmsAt) || 0);
  return sent ? Math.max(0, Math.ceil((sent + 10 * 60000 - now) / 60000)) : 0;
};

export const cancelPaymentLink = (key, reason) => call('/api/payment-links/close', { method: 'POST', body: JSON.stringify({ key, reason }) });

/** The latest request of one kind for an order (pending first), from ship-rules' `requests`. */
export const requestOf = (requests = [], kind) => {
  const mine = requests.filter(r => r.kind === kind).sort((a, b) => String(b.at || '').localeCompare(String(a.at || '')));
  return mine.find(r => r.status === 'pending') || mine[0] || null;
};
